import { FC, ReactNode, useMemo, useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import { CheckPicker, Loader } from 'rsuite'
import { useClearRefinements, useRefinementList } from 'react-instantsearch'
import sortBy from 'lodash/sortBy'
import MinusIcon from '@rsuite/icons/Minus'
import PlusIcon from '@rsuite/icons/Plus'
import groupBy from 'lodash/groupBy'
import { ItemDataType } from 'rsuite/esm/@types/common'

import useIsOnMobile from '../../../services/useIsOnMobile'
import useFiltersClose from '../../hooks/useFiltersClose'
import { useApi } from '../../../services/useApi'
import useUrls from '../../../services/useUrls'
import { createUrlParams } from '../../../lib/params'
import { getPickerValue, handlePickerChange } from './util'
import { ProductAttribute } from '../../../utils/types/Product'
import { FilterItem, isDefined } from '../../../utils/types/misc'

import styles from '../../../styles/CatalogFilters.module.less'

interface SizePickerProps {
  label: string
  shouldCloseFilters: boolean
  filterActions?: {
    updateAppliedFilter?: (filterKey: string, filterItem: FilterItem) => void
    removeAppliedFilter?: (filterKey: string) => void
    removeAppliedFilterItem?: (filterKey: string, value: string | number) => void
  }
}

const SizePicker: FC<SizePickerProps> = (props) => {
  const {
    label,
    shouldCloseFilters,
    filterActions: {
      updateAppliedFilter,
      removeAppliedFilter,
      removeAppliedFilterItem,
    } = {},
  } = props

  const [displayedGroup, setDisplayedGroup] = useState<0 | 1>(0)
  const { t } = useTranslation('checkout')
  const { getResource } = useApi()
  const { pushT } = useUrls()
  const { query, asPath } = useRouter()
  const initializedRef = useRef(false)
  const isOpen = useFiltersClose(shouldCloseFilters)
  const isOnMobile = useIsOnMobile()

  const filterCode = 'f_filter-size'
  const pickerValue = useMemo(() => getPickerValue(query, filterCode), [query])

  const filtersRef = useRef<HTMLDivElement>(null)
  const containerRef = filtersRef.current || undefined

  const {
    refine: clear,
  } = useClearRefinements({
    includedAttributes: ['filter-size-alpha', 'filter-size-numeric'],
  })
  const {
    items: itemsAlpha,
    refine: refineAlpha,
  } = useRefinementList({
    attribute: 'filter-size-alpha',
    operator: 'or',
    limit: 100,
  })
  const {
    items: itemsNumeric,
    refine: refineNumeric,
  } = useRefinementList({
    attribute: 'filter-size-numeric',
    operator: 'or',
    limit: 100,
  })

  const {
    data: attributes,
    isLoading,
  } = getResource<ProductAttribute>('attribute', createUrlParams({
    filter: {
      '||': [
        { '==': { field: 'attribute.type', value: 'filter-size-alpha' } },
        { '==': { field: 'attribute.type', value: 'filter-size-numeric' } },
      ],
    },
    page: { limit: '250' },
  }))

  useEffect(() => {
    // Refine instantsearch filter items when loading page
    const refinedAlpha = itemsAlpha.filter((item) => item.isRefined)
    const refinedNumeric = itemsAlpha.filter((item) => item.isRefined)

    const hasNoRefinements = (refinedAlpha.length + refinedNumeric.length) === 0
    const hasPickerValues = pickerValue.length !== 0
    if (!initializedRef.current && !isLoading && hasNoRefinements && hasPickerValues) {
      // Only trigger "on load" and not on later url changes
      initializedRef.current = true
      attributes
        .filter((attribute) => pickerValue.includes(attribute.id))
        .filter((attribute) => attribute['attribute.type'] === 'filter-size-alpha')
        .filter(isDefined)
        .forEach((item) => {
          refineAlpha(item['attribute.label'])
          updateAppliedFilter?.(filterCode, {
            label: item['attribute.label'] || '',
            value: item.id,
          })
        })
      attributes
        .filter((attribute) => pickerValue.includes(attribute.id))
        .filter((attribute) => attribute['attribute.type'] === 'filter-size-numeric')
        .filter(isDefined)
        .forEach((item) => {
          refineNumeric(item['attribute.label'])
          updateAppliedFilter?.(filterCode, {
            label: item['attribute.label'] || '',
            value: item.id,
          })
        })
    }
  }, [
    itemsAlpha,
    itemsNumeric,
    pickerValue,
    isLoading,
    refineAlpha,
    refineNumeric,
    updateAppliedFilter,
  ])

  const data = attributes
    .filter((attr) => (
      itemsAlpha.concat(itemsNumeric).find((item) => item.label === attr['attribute.label'])
    ))
    .map((attr) => ({
      label: attr['attribute.label'],
      value: attr.id,
      type: attr['attribute.type'],
    }))

  const handleChange = (newValue: string[]) => {
    initializedRef.current = true
    const isClear = newValue.length < pickerValue.length
    const isNew = (id: string) => !pickerValue.includes(id)
    if (isClear) {
      // Clear state from instantsearch
      clear()

      // Clear from filter modal state
      if (newValue.length === 0 && removeAppliedFilter) {
        removeAppliedFilter(filterCode)
      } else if (removeAppliedFilterItem) {
        // Can use find since the picker only supports removing a single value at a time
        const removedValue = pickerValue.find((id) => !newValue.includes(id)) || ''
        removeAppliedFilterItem(filterCode, removedValue)
      }
    }
    attributes
      .filter((attribute) => (
        newValue.includes(attribute.id) && (isClear || isNew(attribute.id))))
      .filter((attribute) => attribute['attribute.type'] === 'filter-size-alpha')
      .filter(isDefined)
      .forEach((attribute) => {
        refineAlpha(attribute['attribute.label'])
        if (updateAppliedFilter) {
          updateAppliedFilter(filterCode, {
            label: attribute['attribute.label'] || '',
            value: attribute.id,
          })
        }
      })
    attributes
      .filter((attribute) => (
        newValue.includes(attribute.id) && (isClear || isNew(attribute.id))))
      .filter((attribute) => attribute['attribute.type'] === 'filter-size-numeric')
      .filter(isDefined)
      .forEach((attribute) => {
        refineNumeric(attribute['attribute.label'])
        if (updateAppliedFilter) {
          updateAppliedFilter(filterCode, {
            label: attribute['attribute.label'] || '',
            value: attribute.id,
          })
        }
      })
    handlePickerChange(pushT, query, asPath, filterCode)(newValue)
  }

  const renderMenu = (menu: ReactNode) => {
    if (isLoading) {
      return (
        <Loader
          className={`${styles.loader}`}
          content="Loading..."
        />
      )
    }
    return (
      <div className={styles['size-menu']}>
        {menu}
      </div>
    )
  }

  const renderMenuItem = (itemLabel: ReactNode): ReactNode => (
    <div className={styles['size-item']}>{itemLabel}</div>
  )

  const renderMenuGroup = (title: ReactNode): ReactNode => {
    const labels = {
      'filter-size-alpha': `International ${t('Size', { amount: 10 })}`,
      'filter-size-numeric': `EU ${t('Size', { amount: 10 })}`,
    }
    return (
      <span>{labels[title as keyof typeof labels]}</span>
    )
  }

  const alphaSizeOrder = (item: { label: string, type: string }): number => {
    if (item.type === 'filter-size-numeric') {
      return Number(item.label)
    }

    // Hard coded ordering of sizes
    const order = ['5XS', '4XS', '3XS', '2XS', 'XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL', '2XL', '3XL', '4XL', '5XL']
    if (order.includes(item.label)) {
      // Sort alpha after numeric, thus add a large number to all
      return order.indexOf(item.label) + 1000
    }
    return order.length + 1000
  }
  const sortedData = sortBy(data, [(item) => alphaSizeOrder(item)])

  if (isOnMobile) {
    return (
      <div className={`rs-picker-menu ${styles['mobile-picker']}`}>
        <div className="rs-pick-checker-menu">
          {renderMenu(
            <div className="rs-picker-check-menu rs-picker-check-menu-items rs-picker-check-menu-grouped">
              {Object.entries(groupBy(sortedData, ({ type }: { type: string }) => type))
                .map(([group, items]: [string, ItemDataType<string>[]], index) => (
                  [
                    // eslint-disable-next-line max-len
                    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-noninteractive-element-interactions
                    <div
                      role="group"
                      className="rs-picker-menu-group"
                      onClick={() => setDisplayedGroup((index % 2) as 0 | 1)}
                    >
                      <div className="rs-picker-menu-group-title">
                        {displayedGroup === index ? <MinusIcon /> : <PlusIcon />}
                        {renderMenuGroup(group)}
                      </div>
                    </div>,
                    ...(displayedGroup === index ? items.map((item) => (
                      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                      <div
                        role="option"
                        aria-selected={pickerValue.includes(item.value || '')}
                        aria-disabled="false"
                        tabIndex={-1}
                        onClick={() => {
                          if (pickerValue.includes(item.value || '')) {
                            handleChange(pickerValue.filter((oldVAlue) => oldVAlue !== item.value))
                          } else {
                            handleChange([...pickerValue, item.value || ''])
                          }
                        }}
                      >
                        <div className={`rs-check-item rs-checkbox ${pickerValue.includes(item.value || '') ? 'rs-checkbox-checked' : ''}`}>
                          <div className="rs-checkbox-checker">
                            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                            <label>
                              {renderMenuItem(item.label)}
                            </label>
                          </div>
                        </div>
                      </div>
                    )) : []),
                  ]
                ))}
            </div>,
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      ref={filtersRef}
      className="relative"
      data-testid={`${filterCode}-picker-container`}
    >
      <CheckPicker
        data={sortedData}
        value={pickerValue}
        placeholder={label}
        label={pickerValue.length ? label : undefined}
        groupBy="type"
        cleanable={false}
        open={isOpen}
        block
        placement="bottomEnd"
        container={containerRef}
        searchable={false}
        renderMenu={renderMenu}
        renderMenuItem={renderMenuItem}
        renderMenuGroup={renderMenuGroup}
        onChange={handleChange}
      />
    </div>
  )
}

export default SizePicker
